import { useEffect, useRef, type ReactElement } from 'react';
import {
  gradeCreditRangesTableWrapperStyle,
  gradeCreditRangesTableTableStyle,
  gradeCreditRangesTableHeaderCellStyle,
  gradeCreditRangesTableBodyCellStyle,
  gradeCreditRangesTableGradeColStyle,
  gradeCreditRangesTableCreditsColStyle,
  gradeCreditRangesTableInputStyle,
  gradeCreditRangesTableValidationSummaryStyle,
} from './grade-credit-ranges-table.styles';
import {
  gradeCreditStringsMinExceedsMax,
  type GradeCreditStringRow,
} from '@/utils/grade-credit-table';
import { sanitizeCreditsFromSource } from '@/utils/sanitize-credits-input';

export type { GradeCreditStringRow };

export interface GradeCreditRangesTableLabels {
  gradeHeader: string;
  minHeader: string;
  maxHeader: string;
  placeholderMin: string;
  placeholderMax: string;
}

export interface GradeCreditRangesTableProps {
  /** Defaults to grades 6–12 when omitted. */
  grades?: readonly number[];
  rows: Record<number, GradeCreditStringRow>;
  onCellChange: (grade: number, field: 'min' | 'max', value: string) => void;
  labels: GradeCreditRangesTableLabels;
  /** When set, shown below the table (e.g. configurations panel). */
  validationSummaryMessage?: string;
  /** Optional id prefix for inputs (plan form vs configurations). */
  inputIdPrefix?: string;
  /** Optional test id on the wrapper (plan form accessibility / tests). */
  wrapperTestId?: string;
  /** Optional accessible name for the grid region. */
  ariaLabel?: string;
  /** Per input (defaults to Grade N minimum/maximum credits). */
  formatInputAriaLabel?: (grade: number, field: 'min' | 'max') => string;
}

/**
 * Grade / minimum credits / maximum credits table (same markup and inline styles as configurations
 * grade credit alerts panel).
 *
 * Known parity notes (same as configurations table; fixes deferred unless product asks):
 * - Row error styling only when both cells parse as finite numbers and min > max; no separate negative-value message (relies on `min={0}` on inputs).
 * - Non-finite text in a cell is treated like empty for min/max comparison until the value parses.
 * - Table width is fixed at 600px with `maxWidth: 100%` on the wrapper for small viewports.
 */
export function GradeCreditRangesTable({
  grades = [6, 7, 8, 9, 10, 11, 12],
  rows,
  onCellChange,
  labels,
  validationSummaryMessage,
  inputIdPrefix = 'grade-credit-ranges',
  wrapperTestId,
  ariaLabel,
  formatInputAriaLabel = (grade, field) =>
    `Grade ${grade} ${field === 'min' ? 'minimum' : 'maximum'} credits`,
}: GradeCreditRangesTableProps): ReactElement {
  const showSummary = Boolean(validationSummaryMessage);
  const validationSummaryRef = useRef<HTMLParagraphElement>(null);
  const prevShowSummary = useRef(false);

  useEffect(() => {
    const becameVisible = showSummary && !prevShowSummary.current;
    prevShowSummary.current = showSummary;
    if (!becameVisible || !validationSummaryRef.current) {
      return;
    }
    requestAnimationFrame(() => {
      const node = validationSummaryRef.current;
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [showSummary]);

  return (
    <div>
      <div style={gradeCreditRangesTableWrapperStyle} data-testid={wrapperTestId}>
        <table style={gradeCreditRangesTableTableStyle} role="grid" aria-label={ariaLabel}>
          <thead>
            <tr>
              <th
                scope="col"
                style={{
                  ...gradeCreditRangesTableHeaderCellStyle,
                  ...gradeCreditRangesTableGradeColStyle,
                }}
              >
                {labels.gradeHeader}
              </th>
              <th
                scope="col"
                style={{
                  ...gradeCreditRangesTableHeaderCellStyle,
                  ...gradeCreditRangesTableCreditsColStyle,
                }}
              >
                {labels.minHeader}
              </th>
              <th
                scope="col"
                style={{
                  ...gradeCreditRangesTableHeaderCellStyle,
                  ...gradeCreditRangesTableCreditsColStyle,
                }}
              >
                {labels.maxHeader}
              </th>
            </tr>
          </thead>
          <tbody>
            {grades.map((grade) => {
              const row = rows[grade] ?? { min: '', max: '' };
              const rowError = gradeCreditStringsMinExceedsMax(row.min, row.max);
              return (
                <tr key={grade}>
                  <td
                    style={{
                      ...gradeCreditRangesTableBodyCellStyle,
                      ...gradeCreditRangesTableGradeColStyle,
                    }}
                  >
                    {grade}
                  </td>
                  <td
                    style={{
                      ...gradeCreditRangesTableBodyCellStyle,
                      ...gradeCreditRangesTableCreditsColStyle,
                    }}
                  >
                    <input
                      id={`${inputIdPrefix}-${grade}-min`}
                      type="number"
                      step="any"
                      inputMode="decimal"
                      min={0}
                      value={row.min}
                      onChange={(e) =>
                        onCellChange(grade, 'min', sanitizeCreditsFromSource(e.target.value))
                      }
                      placeholder={labels.placeholderMin}
                      style={{
                        ...gradeCreditRangesTableInputStyle,
                        ...(rowError ? { borderColor: '#D32F2F' } : {}),
                      }}
                      aria-label={formatInputAriaLabel(grade, 'min')}
                    />
                  </td>
                  <td
                    style={{
                      ...gradeCreditRangesTableBodyCellStyle,
                      ...gradeCreditRangesTableCreditsColStyle,
                    }}
                  >
                    <input
                      id={`${inputIdPrefix}-${grade}-max`}
                      type="number"
                      step="any"
                      inputMode="decimal"
                      min={0}
                      value={row.max}
                      onChange={(e) =>
                        onCellChange(grade, 'max', sanitizeCreditsFromSource(e.target.value))
                      }
                      placeholder={labels.placeholderMax}
                      style={{
                        ...gradeCreditRangesTableInputStyle,
                        ...(rowError ? { borderColor: '#D32F2F' } : {}),
                      }}
                      aria-label={formatInputAriaLabel(grade, 'max')}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {showSummary && (
        <p
          ref={validationSummaryRef}
          style={gradeCreditRangesTableValidationSummaryStyle}
          role="alert"
        >
          {validationSummaryMessage}
        </p>
      )}
    </div>
  );
}
